import React, { useState, useEffect } from 'react';
import { ArrowRight, MapPin, Mail, Linkedin, Calendar, Search } from 'lucide-react';
import { personalInfo, projects } from '../data/mock';
import profilePhoto from '../data/my_photo.png';

const titles = [
  "DevOps Specialist",
  "Sr DevOps Specialist",
  "Architect",
  "SRE",
  "Sr Software Developer"
];

const Hero = () => {

  const [displayText, setDisplayText] = useState('');
  const [titleIndex, setTitleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(60);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    const currentTitle = titles[titleIndex];

    const handleTyping = () => {
      if (!isDeleting) {
        // Typing forward
        if (displayText.length < currentTitle.length) {
          setDisplayText(currentTitle.substring(0, displayText.length + 1));
          setTypingSpeed(60); // Faster typing
        } else {
          // Finished typing, wait before deleting
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        // Deleting backward
        if (displayText.length > 0) {
          setDisplayText(currentTitle.substring(0, displayText.length - 1));
          setTypingSpeed(30); // Faster deletion
        } else {
          // Finished deleting, move to next title
          setIsDeleting(false);
          setTitleIndex((prev) => (prev + 1) % titles.length);
          setTypingSpeed(300); // Shorter pause before typing next title
        }
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayText, isDeleting, titleIndex, typingSpeed]);

  // Function to colorize words with rotating colors
  const colorizeText = (text) => {
    const colors = ['text-blue-500', 'text-red-500', 'text-green-500'];
    const words = text.split(' ');

    return words.map((word, index) => (
      <span key={index} className={colors[index % colors.length]}>
        {word}{index < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  const scrollToProjects = () => {
    const element = document.querySelector('#projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSearch = () => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchError('Enter a keyword to search.');
      return;
    }
    const normalize = (s) =>
      s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const q = normalize(query);
    const qTokens = q.split(' ').filter(Boolean);
    const editDistance = (a, b) => {
      if (a === b) return 0;
      const dp = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
      for (let i = 0; i <= a.length; i++) dp[i][0] = i;
      for (let j = 0; j <= b.length; j++) dp[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }
      return dp[a.length][b.length];
    };
    const fuzzyMatch = (text) => {
      if (!q) return false;
      const t = normalize(text);
      if (t.includes(q)) return true;
      const tokens = t.split(' ').filter(Boolean);
      return qTokens.some((qt) =>
        tokens.some((tok) => {
          const maxDist = qt.length >= 7 ? 2 : 1;
          return editDistance(qt, tok) <= maxDist;
        })
      );
    };
    const matches = projects.filter((project) => {
      const haystack = [
        project.title,
        project.problem,
        project.solution,
        project.results,
        ...(project.technologies || [])
      ].join(' ');
      return fuzzyMatch(haystack);
    });
    if (matches.length === 0) {
      setSearchError('No matching project found.');
      return;
    }
    setSearchError('');
    window.dispatchEvent(new CustomEvent('project-search', { detail: { query } }));
    const projectsSection = document.querySelector('#projects');
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden pt-12">
      {/* Animated background shapes */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-5xl mx-auto px-6 py-12 relative z-10">
        {/* Project Search */}
        <div className="w-full flex justify-center mb-8">
          <div className="max-w-3xl w-full">
            <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-shadow">
              <Search className="text-blue-500" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (searchError) setSearchError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="Search projects (e.g., Kubernetes, RAG, Terraform)..."
                className="flex-1 outline-none text-gray-700 placeholder-gray-400 bg-transparent"
              />
              <button
                onClick={handleSearch}
                className="px-5 py-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold hover:from-blue-600 hover:to-green-600 transition-all"
              >
                Search
              </button>
            </div>
            {searchError && (
              <div className="mt-2 text-sm text-red-500 text-center">{searchError}</div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8">
          {/* Left Column - Text Content */}
          <div className="space-y-6">

            {/* Greeting */}
            <div className="inline-block">
              <span className="text-blue-500 text-sm font-semibold px-4 py-2 bg-blue-50 rounded-full border border-blue-200">
                Hello, I'm
              </span>
            </div>

            {/* Name - Google Colors */}
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <span className="text-blue-500">{personalInfo.name.split(' ')[0]}</span>
              <br />
              <span className="text-red-500">{personalInfo.name.split(' ')[1]}</span>
            </h1>

            {/* Title with Typing Animation */}
            <h2 className="text-2xl md:text-3xl text-gray-700 font-semibold min-h-[2.5rem] md:min-h-[3rem]">
              <span className="relative inline-block px-4 py-2">
                {/* Hand-drawn highlight splash */}
                <svg
                  className="absolute inset-0 w-full h-full -z-10"
                  viewBox="0 0 300 60"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5,25 Q10,15 20,18 T50,22 Q80,25 110,20 T170,24 Q210,27 240,23 T280,25 Q290,28 295,35 L295,45 Q290,50 280,48 T240,45 Q210,42 170,46 T110,44 Q80,40 50,43 T20,45 Q10,48 5,40 Z"
                    fill="url(#highlightGradient)"
                    opacity="0.6"
                  />
                  <defs>
                    <linearGradient id="highlightGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#fef08a', stopOpacity: 1 }} />
                      <stop offset="50%" style={{ stopColor: '#fde047', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#fef08a', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                </svg>
                {colorizeText(displayText)}
                <span className="text-green-500 animate-blink">|</span>
              </span>
            </h2>

            {/* Summary */}
            <p className="text-gray-600 text-base leading-relaxed">
              {(() => {
                const marker = "9+ years";
                const summary = personalInfo.summary || "";
                if (!summary.includes(marker)) return summary;
                const parts = summary.split(marker);
                return (
                  <>
                    {parts[0]}
                    <span className="summary-3d">{marker}</span>
                    {parts.slice(1).join(marker)}
                  </>
                );
              })()}
            </p>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-4 pt-2">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={18} className="text-blue-500" />
                <span className="text-sm">{personalInfo.location}</span>
              </div>
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center gap-2 text-gray-600 hover:text-red-500 transition-colors"
              >
                <Mail size={18} className="text-red-500" />
                <span className="text-sm">{personalInfo.email}</span>
              </a>
              <a
                href={`https://${personalInfo.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-600 hover:text-blue-500 transition-colors"
              >
                <Linkedin size={18} className="text-blue-500" />
                <span className="text-sm">LinkedIn</span>
              </a>
              <a
                href="https://calendly.com/mpavan-k-15/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-600 hover:text-green-500 transition-colors"
              >
                <Calendar size={18} className="text-green-500" />
                <span className="text-sm">Calendly</span>
              </a>
            </div>

            {/* CTA Button */}
            <div className="pt-2">
              <button
                onClick={scrollToProjects}
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                View Projects
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right Column - Photo */}
          <div className="flex justify-center flex-shrink-0">
            <div className="relative group">
              {/* Gradient border container - CIRCULAR */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-red-500 via-yellow-500 to-green-500 rounded-full blur opacity-75 animate-gradient-rotate"></div>

              {/* Photo container - CIRCULAR */}
              <div className="relative">
                <div className="bg-white p-2 rounded-full">
                  <img
                    src={profilePhoto}
                    alt={personalInfo.name}
                    className="w-72 h-72 md:w-96 md:h-96 object-cover rounded-full shadow-2xl"
                    style={{ objectPosition: "center 10%" }}
                  />
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-500 to-green-500 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-br from-red-500 to-yellow-500 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Add custom CSS for gradient animation and typing cursor */}
      <style jsx>{`
        @keyframes gradient-rotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-rotate {
          background-size: 200% 200%;
          animation: gradient-rotate 3s ease infinite;
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        .summary-3d {
          display: inline-block;
          font-weight: 900;
          font-size: 1.10em;
          color: #111827;
          background: #fde68a;
          padding: 0 3px;
          border-radius: 10px;
          border: 2px solid #111827;
          box-shadow: 2px 2px 0 #111827;
          transform: rotate(-2deg);
          animation: summary-bounce 1.6s ease-in-out infinite;
        }
        @keyframes summary-bounce {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-3px) rotate(2deg); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
